import { Config } from 'config/envConfig';
import React, { useState } from 'react';
import { Button, TextArea, Input, MoveCreditsPrompt, Dropdown } from '@quyva/shared-ui';
import { getUrl } from '../../../pdfDisplay/utils';
import { validateWebsiteUrl } from '@quyva/common';

export const CreateOrganizationForm = ({ onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [website, setWebsite] = useState('');
    const [address, setAddress] = useState('');
    const [industry, setIndustry] = useState('');
    const [currency, setCurrency] = useState('INR');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activePlan, setActivePlan] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [moveCredits, setMoveCredits] = useState(true);

    const createOrg = async () => {
        setIsLoading(true);
        setError('');
        try {
            const user = JSON.parse(localStorage.getItem('current'));
            const response = await fetch(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/create`
                ),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: encodeURIComponent(user.token),
                    },
                    body: JSON.stringify({
                        name,
                        website,
                        address,
                        industry,
                        currency,
                        moveCredits: activePlan ? moveCredits : false,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message || 'Failed to create organization'
                );
            }

            // Call the success callback instead of reloading
            if (onSuccess) {
                onSuccess(data);
            }
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (name.trim() === '') {
            setError('Organization name cannot be empty');
            return;
        }
        if (website && !validateWebsiteUrl(website)) {
            setError(
                'Please enter a valid website URL (e.g., https://example.com)'
            );
            return;
        }

        setIsLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('current'));
            const subRes = await fetch(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/user-active-subscription`
                ),
                { headers: { Authorization: encodeURIComponent(user.token) } }
            );
            const subData = await subRes.json();
            if (subData.hasActivePlan) {
                setActivePlan(subData);
                setMoveCredits(true);
                setShowConfirm(true);
            } else {
                await createOrg();
            }
        } catch {
            await createOrg();
        } finally {
            setIsLoading(false);
        }
    };

    if (showConfirm && activePlan) {
        return (
            <div className='p-6 space-y-6'>
                <MoveCreditsPrompt
                    activePlan={activePlan}
                    moveCredits={moveCredits}
                    onChange={setMoveCredits}
                    defaultPlanLabel='FREE'
                />
                {error && (
                    <div className='text-texts-red400 text-sm'>{error}</div>
                )}
                <div className='flex justify-end space-x-4'>
                    <Button
                        text='Back'
                        onClick={() => {
                            setShowConfirm(false);
                            setError('');
                        }}
                        type='secondary'
                        disabled={isLoading}
                    />
                    <Button
                        text={isLoading ? 'Creating...' : 'Create Organization'}
                        type='success'
                        onClick={createOrg}
                        disabled={isLoading}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className='p-4 space-y-4'>
            <form onSubmit={handleSubmit} className='space-y-6'>
                <div className='space-y-2'>
                    <label className='block text-sm font-medium text-texts-secondary300'>
                        Organization Name
                    </label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder='Required'
                        className='mt-1 truncate text-base'
                    />
                </div>
                <div className='space-y-2'>
                    <label className='block text-sm font-medium text-texts-secondary300'>
                        Website
                    </label>
                    <Input
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder='Optional - https://example.com'
                        className='mt-1 truncate text-base'
                    />
                </div>
                <div className='space-y-2'>
                    <label className='block text-sm font-medium text-texts-secondary300'>
                        Address
                    </label>
                    <TextArea
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className='mt-1 block w-full rounded-xl border p-3 focus:outline-backgrounds-primary300'
                        placeholder='Optional'
                        rows={6}
                    />
                </div>
                <div className='space-y-2'>
                    <label className='block text-sm font-medium text-texts-secondary300'>
                        Industry
                    </label>
                    <Input
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        placeholder='Optional'
                        className='mt-1 truncate text-base'
                    />
                </div>
                <div className='space-y-2'>
                    <label className='block text-sm font-medium text-texts-secondary300'>
                        Billing Currency
                    </label>
                    <Dropdown
                        dropDownList={['INR', 'USD'].map((c) => ({
                            label: c,
                            action: () => setCurrency(c),
                            selected: currency === c,
                        }))}
                        buttonText={currency}
                        showButtonText={true}
                        listTitleText='Billing Currency'
                        placement='bottomRight'
                        buttonClassName='w-full !rounded-xl font-semibold flex justify-start items-center px-3 py-2.5 border backgrounds-border shadow-inner outline-none text-texts-tertiary bg-backgrounds-white hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 cursor-pointer text-base'
                    />
                </div>
                {error && <div className='text-texts-red400 text-sm'>{error}</div>}
                <div className='flex justify-end space-x-4 mt-6'>
                    <Button text='Cancel' onClick={onClose} type='danger' />
                    <Button
                        text={isLoading ? 'Creating...' : 'Create Organization'}
                        type='success'
                        disabled={isLoading}
                        submit={true}
                    />
                </div>
            </form>
        </div>
    );
};
